import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import React, { useCallback } from "react";
import TimePicker from "./TimePicker";
import {
  DEFAULT_DATE_STRING,
  HourMinuteOption,
  parseTimeFromDateString,
  parseZoneDayjs,
  Period,
  PERIODS,
  toStandardHours,
} from "./utils";

export interface TimePickerFromDateStringProps {
  id: string;
  dateString?: string | null;
  defaultDateString?: string;
  utc?: boolean;
  onChange: (isoString?: string) => void;
  className?: string;
}

/**
 * Time picker that reads and writes ISO 8601 date strings.
 *
 * Use this when your time state is stored as an ISO string (common in forms
 * and legacy data). Supplies a sensible `defaultDateString` (today 08:00 UTC)
 * when no date is yet selected.
 *
 * Supports both UTC wall-clock (`utc={true}`) and parseZone modes
 * (preserves the embedded timezone offset of the ISO string).
 */
export const TimePickerFromDateString = ({
  id,
  dateString,
  defaultDateString = DEFAULT_DATE_STRING,
  utc,
  onChange,
  className,
}: TimePickerFromDateStringProps): JSX.Element => {
  const effectiveDate = dateString || defaultDateString || "";
  const { hours, minutes, period } = parseTimeFromDateString(
    effectiveDate,
    utc
  );

  const maker = useCallback(
    (ds: string): Dayjs => (utc ? dayjs.utc(ds) : parseZoneDayjs(ds)),
    [utc]
  );

  const handleHoursChange = useCallback(
    (selectedItem: HourMinuteOption) => {
      if (!selectedItem) return;
      const currentPeriod = maker(dateString || defaultDateString || "").format(
        "A"
      ) as Period;
      const newHours = toStandardHours(selectedItem.value, currentPeriod);
      const newMinutes = maker(dateString || defaultDateString || "").minute();
      if (dateString) {
        onChange(
          maker(dateString)
            .hour(newHours)
            .minute(newMinutes)
            .second(0)
            .millisecond(0)
            .toISOString()
        );
      }
    },
    [onChange, dateString, defaultDateString, maker]
  );

  const handleMinutesChange = useCallback(
    (selectedItem: HourMinuteOption) => {
      if (!selectedItem || !dateString) return;
      const currentHours = maker(dateString).hour();
      const newMinutes = selectedItem.value;
      onChange(
        maker(dateString)
          .hour(currentHours)
          .minute(newMinutes)
          .second(0)
          .millisecond(0)
          .toISOString()
      );
    },
    [onChange, dateString, maker]
  );

  const handleTimePeriodChange = useCallback(
    (newPeriod: Period) => {
      if (!dateString) return;
      const d = maker(dateString);
      const hrs = d.hour();
      const mins = d.minute();

      if (newPeriod === PERIODS.AM && hrs >= 12) {
        onChange(
          maker(dateString)
            .hour(hrs - 12)
            .minute(mins)
            .second(0)
            .millisecond(0)
            .toISOString()
        );
      } else if (newPeriod === PERIODS.PM && hrs <= 12) {
        onChange(
          maker(dateString)
            .hour(hrs + 12)
            .minute(mins)
            .second(0)
            .millisecond(0)
            .toISOString()
        );
      }
    },
    [onChange, dateString, maker]
  );

  return (
    <TimePicker
      id={id}
      hours={hours}
      minutes={minutes}
      period={period}
      onHoursChange={handleHoursChange}
      onMinutesChange={handleMinutesChange}
      onTimePeriodChange={handleTimePeriodChange}
      className={className}
    />
  );
};

export default TimePickerFromDateString;
